import { CURRENCY_CODE } from "../constants/DeliveryConstants";
import { getDiscountPriceWithDefault } from "./product";
import {
  ANALYTICS_ADD_TO_CART,
  ANALYTICS_SELECT_ITEM,
  RELATED_PRODUCTS_LIST_ID,
  RELATED_PRODUCTS_LIST_NAME,
} from "../constants/AnalyticsConstants";
import dayjs from "dayjs";
import { logEvent } from "firebase/analytics";
import { initializeAnalytics } from "../services/firebase";

// Helper to get analytics instance or initialize it if not available
let analyticsInstance = null;

const getAnalytics = async () => {
  if (!analyticsInstance) {
    try {
      analyticsInstance = await initializeAnalytics();
    } catch (error) {
      console.warn("Analytics not initialized:", error);
      return null;
    }
  }
  return analyticsInstance;
};

// Helper function to log events safely
export const safeLogEvent = async (eventName, eventParams) => {
  const analytics = await getAnalytics();
  if (analytics) {
    logEvent(analytics, eventName, eventParams);
  }
};

/**
 * Triggers select_item event when clicking on related product
 **/
export const setSelectRelatedProductAnalytics = (product, index) => {
  safeLogEvent(ANALYTICS_SELECT_ITEM, {
    item_list_id: RELATED_PRODUCTS_LIST_ID,
    item_list_name: RELATED_PRODUCTS_LIST_NAME,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        currency: CURRENCY_CODE,
        discount:
          product.price -
          getDiscountPriceWithDefault(product.price, product.discount),
        index: index,
        item_list_id: RELATED_PRODUCTS_LIST_ID,
        item_list_name: RELATED_PRODUCTS_LIST_NAME,
        price: product.price,
        quantity: 1,
      },
    ],
  });
};

/**
 * Triggers add_to_cart event when adding related product directly to cart
 **/
export const setAddToCartAnalyticsRelatedProducts = (product, index) => {
  safeLogEvent(ANALYTICS_ADD_TO_CART, {
    currency: CURRENCY_CODE,
    value: getDiscountPriceWithDefault(product.price, product.discount),
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        currency: CURRENCY_CODE,
        discount:
          product.price -
          getDiscountPriceWithDefault(product.price, product.discount),
        index: index,
        item_list_id: RELATED_PRODUCTS_LIST_ID,
        item_list_name: RELATED_PRODUCTS_LIST_NAME,
        price: product.price,
        quantity: 1,
      },
    ],
  });
};

/**
 * Dynamic analytics event for 1 specific item
 **/
export const setProductAnalyticsEvent = (
  eventName,
  product,
  quantity,
  categoryName,
) => {
  const value = getDiscountPriceWithDefault(product.price, product.discount);
  safeLogEvent(eventName, {
    currency: CURRENCY_CODE,
    value: !!quantity ? value * quantity : value,
    items: [
      {
        item_id: product.id,
        item_name: product.name,
        item_category: categoryName,
        currency: CURRENCY_CODE,
        discount:
          product.price -
          getDiscountPriceWithDefault(product.price, product.discount),
        index: 0,
        price: product.price,
        quantity: !!quantity ? quantity : 1,
      },
    ],
  });
};

export const setCartAnalyticsEvent = async (eventName, products, props) => {
  const totalValue = products.reduce(
    (total, product) =>
      total +
      getDiscountPriceWithDefault(product.price, product.discount) *
        product.quantity,
    0,
  );

  const items = products.map((product, index) => {
    return {
      item_id: product.id,
      item_name: product.name,
      currency: CURRENCY_CODE,
      discount:
        product.price -
        getDiscountPriceWithDefault(product.price, product.discount),
      price: product.price,
      quantity: product.quantity,
      index,
    };
  });

  safeLogEvent(eventName, {
    currency: "EUR",
    value: !!totalValue ? totalValue : 0,
    items,
    ...props,
  });
};

/**
 * POST Purchase event to FB conversions api
 **/
export const setFacebookPurchaseEvent = (totalValue) => {
  fetch(
    `https://graph.facebook.com/v12.0/${process.env.REACT_APP_FACEBOOK_PIXEL_ID}/events?access_token=${process.env.REACT_APP_FACEBOOK_CONVERSION_API_KEY}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        data: [
          {
            event_name: "Purchase",
            event_time: dayjs().unix(),
            action_source: "website",
            custom_data: {
              currency: CURRENCY_CODE,
              value: totalValue,
            },
          },
        ],
      }),
    },
  ).catch((error) => console.error(error));
};

/**
 * Track when a product information tab is opened
 * @param {String} tabType - Type of tab (e.g., 'description', 'ingredients', 'custom', 'traceability')
 * @param {String} tabTitle - Title of the tab
 * @param {Object} product - Product data
 */
export const trackProductTabOpened = (tabType, tabTitle, product) => {
  safeLogEvent('product_tab_opened', {
    tab_type: tabType,
    tab_title: tabTitle,
    product_id: product.id,
    product_name: product.name,
    product_category: Array.isArray(product.categories)
      ? product.categories[0]
      : product.categories,
    product_price: product.price,
    page_url: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
};
